import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import styles from '../styles/BalanceTransferModal.module.css';
import { useAppToast } from '../hooks/useAppToast';

const formatBalance = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '--';
  }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const TRANSFER_MODES = {
  credit: 'credit',
  debit: 'debit',
};

const BalanceTransferModal = ({
  isOpen,
  onClose,
  toRole,
  toId,
  recipientBalance,
  recipientBalanceCheck,
  onTransfer,
  onTransferSuccess,
}) => {
  const dispatch = useDispatch();
  const { showWarning, showApiError } = useAppToast();
  const [amount, setAmount] = useState('');
  const [transferMode, setTransferMode] = useState(TRANSFER_MODES.credit);
  const [fromRole, setFromRole] = useState('');
  const [fromId, setFromId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const username = localStorage.getItem('username');

    if (storedUser && username) {
      const parsedUser = JSON.parse(storedUser);
      setFromRole(parsedUser.role);
      setFromId(username);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setTransferMode(TRANSFER_MODES.credit);
      setLoading(false);
    }
  }, [isOpen]);

  const handleAmountChange = (e) => {
    const next = e.target.value;
    if (next === '' || /^\d*\.?\d*$/.test(next)) {
      setAmount(next);
    }
  };

  const handleTransfer = async () => {
    const absoluteAmount = parseFloat(amount);
    if (Number.isNaN(absoluteAmount) || absoluteAmount <= 0) {
      showWarning('Please enter a valid amount greater than zero.');
      return;
    }

    const isCredit = transferMode === TRANSFER_MODES.credit;
    const transferAmount = isCredit ? absoluteAmount : -absoluteAmount;

    setLoading(true);

    try {
      let storedUser = {};
      try {
        storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        storedUser = {};
      }

      // Credit: enforce sender balance unless sender.balance_check === false
      if (isCredit && fromRole !== 'super_admin' && storedUser.balance_check !== false) {
        const bal = Number(storedUser.balance);
        if (Number.isFinite(bal) && absoluteAmount > bal) {
          showWarning('Insufficient balance. You cannot transfer more than your available balance.');
          setLoading(false);
          return;
        }
      }

      // Debit: enforce recipient balance unless recipient.balance_check === false
      if (
        !isCredit
        && recipientBalanceCheck !== false
        && recipientBalance !== undefined
        && recipientBalance !== null
      ) {
        const recvBal = Number(recipientBalance);
        if (Number.isFinite(recvBal) && absoluteAmount > recvBal) {
          showWarning('Insufficient recipient balance. Debit cannot exceed what the recipient has.');
          setLoading(false);
          return;
        }
      }

      const transferResult = await onTransfer({ fromRole, fromId, toRole, toId, amount: transferAmount });

      if (typeof onTransferSuccess === 'function') {
        onTransferSuccess(transferResult);
      }

      // Keep header balance in sync only after confirmed API success (see throwUnlessApiSuccess in callers).
      if (fromRole !== 'super_admin') {
        const apiBalance = transferResult?.newSenderBalance;
        const parsedApiBalance = Number(apiBalance);
        if (!Number.isFinite(parsedApiBalance)) {
          throw new Error('Invalid balance in server response');
        }
        dispatch({ type: 'UPDATE_AUTH_BALANCE', payload: parsedApiBalance });
      }

      onClose();
    } catch (err) {
      showApiError(err, 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Balance Transfer</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalBody}>
          {/* <div className={styles.formGroup}>
            <label className={styles.label}>From Role</label>
            <input
              type="text"
              value={fromRole}
              readOnly
              className={styles.input}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Sender ID</label>
            <input
              type="text"
              value={fromId}
              readOnly
              className={styles.input}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>To Role</label>
            <input
              type="text"
              value={toRole}
              readOnly
              className={styles.input}
            />
          </div>*/}
          <div className={styles.formGroup}>
            <label className={styles.label}>Recipient ID</label>
            <input
              type="text"
              value={toId}
              readOnly
              className={styles.input}
            />
          </div>
          {recipientBalance !== undefined && recipientBalance !== null && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Recipient Balance</label>
              <input
                type="text"
                value={formatBalance(recipientBalance)}
                readOnly
                className={styles.input}
              />
            </div>
          )}
          <div className={styles.formGroup}>
            <label className={styles.label}>Transfer Type</label>
            <div className={styles.modeToggle} role="group" aria-label="Transfer type">
              <button
                type="button"
                className={`${styles.modeButton} ${transferMode === TRANSFER_MODES.credit ? styles.modeButtonActive : ''}`}
                onClick={() => setTransferMode(TRANSFER_MODES.credit)}
                disabled={loading}
              >
                Credit
              </button>
              <button
                type="button"
                className={`${styles.modeButton} ${transferMode === TRANSFER_MODES.debit ? styles.modeButtonActiveDebit : ''}`}
                onClick={() => setTransferMode(TRANSFER_MODES.debit)}
                disabled={loading}
              >
                Debit
              </button>
            </div>
            <p className={styles.modeHint}>
              {transferMode === TRANSFER_MODES.credit
                ? 'Add balance to the recipient'
                : 'Deduct balance from the recipient'}
            </p>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Amount <span className={styles.required}>*</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="Enter amount"
              value={amount}
              onChange={handleAmountChange}
              className={styles.input}
              required
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button
            className={`${styles.submitButton} ${transferMode === TRANSFER_MODES.debit ? styles.submitButtonDebit : ''}`}
            onClick={handleTransfer}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className={styles.spinner} size={16} />
                <span>{transferMode === TRANSFER_MODES.credit ? 'Crediting...' : 'Debiting...'}</span>
              </>
            ) : (
              transferMode === TRANSFER_MODES.credit ? 'Credit' : 'Debit'
            )}
          </button>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BalanceTransferModal;
